import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  useTheme,
  Autocomplete,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import Toast from "../components/Toast";
import ReactQuill from "react-quill";
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useUser } from "../context/UserContext";
import { downloadDayPointsTemplate, parseDayPointsExcel } from "../utils/ExcelPrayerService";
import HelpTooltip from "../components/HelpTooltip";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import PrayerWizardSetup from "../components/PrayerWizardSetup";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TuneIcon from "@mui/icons-material/Tune";
import LinkIcon from "@mui/icons-material/Link";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SettingsIcon from "@mui/icons-material/Settings";
import PrayerVideoManager from "../components/PrayerVideoManager";
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import MobilePrayerPreviewModal from "../components/MobilePrayerPreviewModal";
import MobileSpecialPrayerModal from "../components/MobileSpecialPrayerModal";
import VisibilityIcon from "@mui/icons-material/Visibility";

const SUPPORTED_LANGUAGES = [
  { code: 'te', label: 'Telugu (తెలుగు)' },
  { code: 'ta', label: 'Tamil (தமிழ்)' },
  { code: 'ml', label: 'Malayalam (മലയാളം)' },
  { code: 'hi', label: 'Hindi (हिंदी)' },
  { code: 'kn', label: 'Kannada (ಕನ್ನಡ)' },
  { code: 'bn', label: 'Bengali (বাংলা)' },
  { code: 'mr', label: 'Marathi (मराठी)' },
  { code: 'es', label: 'Spanish (Español)' },
];

const DEFAULT_FALLBACK_TEMPLATES = Array.from({ length: 23 }, (_, idx) => {
  const num = idx + 1;
  return {
    id: num,
    title: `Template ${num}`,
    url: `/prayforthenation/template${num}.jpg`,
  };
});

const getTemplateImageUrl = (url, baseUrl) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  if (baseUrl) {
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}${cleanUrl}`;
  }
  return cleanUrl;
};

const AddSpecialPrayer = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isWizardActive, setIsWizardActive] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [prayerPlan, setPrayerPlan] = useState("fixed");
  const [chainDurationHours, setChainDurationHours] = useState("24");
  const _todayStr = new Date().toISOString().split("T")[0];
  const _defaultEndStr = (() => { const d = new Date(); d.setDate(d.getDate() + 20); return d.toISOString().split("T")[0]; })();
  const [fromDate, setFromDate] = useState(_todayStr);
  const [toDate, setToDate] = useState(_defaultEndStr);
  const [numberOfDays, setNumberOfDays] = useState("21");
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [slotDuration, setSlotDuration] = useState("30");
  const [prayerType, setPrayerType] = useState("Group");
  const [talkItOver, setTalkItOver] = useState(true);
  const [prayerFrequency, setPrayerFrequency] = useState("Daily");
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [contentType, setContentType] = useState("yes");
  const [numDays, setNumDays] = useState(0);
  const [currentDay, setCurrentDay] = useState(null);
  const [dayWiseData, setDayWiseData] = useState({});
  const [activeContentLang, setActiveContentLang] = useState("en");
  const [sameContentForAllDays, setSameContentForAllDays] = useState(false);
  const [templatesList, setTemplatesList] = useState(DEFAULT_FALLBACK_TEMPLATES);
  const [selectedTemplateId, setSelectedTemplateId] = useState(1);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const currentActiveTemplate = React.useMemo(() => {
    if (!Array.isArray(templatesList) || templatesList.length === 0) return null;
    return templatesList.find(t => String(t.id) === String(selectedTemplateId)) || templatesList[0] || null;
  }, [templatesList, selectedTemplateId]);

  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('special-prayer'));
  const [activeDraftId, setActiveDraftId] = useState(null);

  const [customTemplateTitle, setCustomTemplateTitle] = useState("");
  const [customTemplateFile, setCustomTemplateFile] = useState(null);
  const [customTemplatePreview, setCustomTemplatePreview] = useState("");
  const [basicInfoModal, setBasicInfoModal] = useState({ open: false, mode: 'example' });


  const [categories, setCategories] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("global");
  const API_URL = process.env.REACT_APP_API_URL;
  const Image_URL = process.env.REACT_APP_Image_URL;
  const { user, isSwitched, effectiveOrgId } = useUser();
  const currentOrgKey = isSwitched ? (effectiveOrgId || user?.org_id) : (user?.role === 1 ? 'global' : user?.org_id);

  // Sync drafts whenever the active organization changes
  useEffect(() => {
    setDraftsList(DraftManager.getDrafts('special-prayer', currentOrgKey));
  }, [currentOrgKey]);

  // Load draft if passed via route navigation
  useEffect(() => {
    if (location.state?.draft) {
      handleContinueDraft(location.state.draft);
    }
  }, [location.state]);

  // Delete confirmation dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Keep latest state in ref to auto-save when closing or leaving
  const isSubmittedRef = useRef(false);
  const latestDataRef = useRef({
    title, description, category, prayerPlan, chainDurationHours, fromDate, toDate, numberOfDays, fromTime, toTime, slotDuration, prayerType, talkItOver, prayerFrequency, daysOfWeek, selectedTemplateId, dayWiseData, activeDraftId, currentOrgKey
  });

  useEffect(() => {
    latestDataRef.current = {
      title, description, category, prayerPlan, chainDurationHours, fromDate, toDate, numberOfDays, fromTime, toTime, slotDuration, prayerType, talkItOver, prayerFrequency, daysOfWeek, selectedTemplateId, dayWiseData, activeDraftId, currentOrgKey
    };
  }, [title, description, category, prayerPlan, chainDurationHours, fromDate, toDate, numberOfDays, fromTime, toTime, slotDuration, prayerType, talkItOver, prayerFrequency, daysOfWeek, selectedTemplateId, dayWiseData, activeDraftId, currentOrgKey]);

  // Check if there is any meaningful draft data entered
  const hasDraftContent = (data) => {
    if (!data) return false;
    if (data.title && data.title.trim().length > 0) return true;
    if (data.description && data.description.trim().length > 0 && data.description !== "<p><br></p>") return true;
    if (data.category && data.category !== "") return true;
    if (data.selectedTemplateId) return true;
    if (data.dayWiseData && Object.keys(data.dayWiseData).length > 0) {
      const hasAnyPointsOrText = Object.values(data.dayWiseData).some(
        (day) =>
          (day.title && day.title.trim().length > 0) ||
          (day.description && day.description.trim().length > 0 && day.description !== "<p><br></p>") ||
          (Array.isArray(day.prayerPoints) && day.prayerPoints.length > 0)
      );
      if (hasAnyPointsOrText) return true;
    }
    if (data.prayerPlan && data.prayerPlan !== "fixed") return true;
    if (data.prayerFrequency && data.prayerFrequency !== "Daily") return true;
    return false;
  };

  // Helper to persist draft when exiting or closing
  const saveDraftOnExit = () => {
    if (isSubmittedRef.current) return false;
    const data = latestDataRef.current;
    if (!hasDraftContent(data)) return false;

    const draftTitle =
      (data.title && data.title.trim()) ||
      (data.category ? `${data.category} Prayer Draft` : "Untitled Prayer Draft");

    const draftPayload = {
      title: data.title?.trim() || draftTitle,
      description: data.description || "",
      category: data.category || "",
      prayerPlan: data.prayerPlan || "fixed",
      chainDurationHours: data.chainDurationHours || "24",
      fromDate: data.fromDate || "",
      toDate: data.toDate || "",
      numberOfDays: data.numberOfDays || "21",
      fromTime: data.fromTime || "00:00",
      toTime: data.toTime || "23:59",
      slotDuration: data.slotDuration || "30",
      prayerType: data.prayerType || "Group",
      talkItOver: data.talkItOver !== undefined ? data.talkItOver : true,
      prayerFrequency: data.prayerFrequency || "Daily",
      daysOfWeek: data.daysOfWeek || [],
      selectedTemplateId: data.selectedTemplateId || "",
      dayWiseData: data.dayWiseData || {},
    };

    const res = DraftManager.saveDraft(
      "special-prayer",
      draftPayload,
      data.activeDraftId,
      data.currentOrgKey
    );

    if (res && res.success && res.draftId) {
      data.activeDraftId = res.draftId;
      setActiveDraftId(res.draftId);
      return true;
    }
    return false;
  };

  // Auto-save draft when closing / leaving page without clicking Save Draft
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraftOnExit();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Auto-save when unmounting (e.g. browser back button or link navigation)
      if (!isSubmittedRef.current) {
        const saved = saveDraftOnExit();
        if (saved) {
          try {
            sessionStorage.setItem(
              "gpn_pending_draft_toast",
              JSON.stringify({
                open: true,
                message: "Draft saved to Prayer Closet successfully",
                severity: "success",
              })
            );
          } catch (e) {}
        }
      }
    };
  }, []);

  const handleClose = () => {
    const saved = saveDraftOnExit();
    const toastMsg = saved ? "Draft saved to Prayer Closet successfully" : null;

    if (toastMsg) {
      try {
        sessionStorage.setItem(
          "gpn_pending_draft_toast",
          JSON.stringify({
            open: true,
            message: toastMsg,
            severity: "success",
          })
        );
      } catch (e) {
        console.error("Failed to store draft toast:", e);
      }
    }

    navigate("/admin/special-prayers", {
      state: toastMsg
        ? {
            toast: {
              open: true,
              message: toastMsg,
              severity: "success",
            },
            refreshDrafts: true,
          }
        : undefined,
    });
  };

  const handleManualSaveDraft = () => {
    const fallbackTitle = (title && title.trim()) || (category ? `${category} Prayer Draft` : "Untitled Prayer Draft");
    const draftPayload = {
      title: title?.trim() || fallbackTitle,
      description: description || '',
      category: category || '',
      prayerPlan: prayerPlan || 'fixed',
      chainDurationHours: chainDurationHours || '24',
      fromDate: fromDate || '',
      toDate: toDate || '',
      numberOfDays: numberOfDays || '21',
      fromTime: fromTime || '00:00',
      toTime: toTime || '23:59',
      slotDuration: slotDuration || '30',
      prayerType: prayerType || 'Group',
      talkItOver: talkItOver !== undefined ? talkItOver : true,
      prayerFrequency: prayerFrequency || 'Daily',
      daysOfWeek: daysOfWeek || [],
      selectedTemplateId: selectedTemplateId || '',
      dayWiseData: dayWiseData || {},
    };

    const res = DraftManager.saveDraft('special-prayer', draftPayload, activeDraftId, currentOrgKey);

    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    setIsWizardActive(false);
    const d = draft.data;
    if (d.title !== undefined) {
      setTitle(d.title === "Untitled Prayer Draft" || d.title.endsWith(" Prayer Draft") ? "" : d.title);
    }
    if (d.description !== undefined) setDescription(d.description);
    if (d.category !== undefined) setCategory(d.category);
    if (d.prayerPlan !== undefined) setPrayerPlan(d.prayerPlan === 'open' ? 'fixed' : d.prayerPlan);
    if (d.chainDurationHours !== undefined) setChainDurationHours(d.chainDurationHours);
    if (d.fromDate !== undefined) setFromDate(d.fromDate);
    if (d.toDate !== undefined) setToDate(d.toDate);
    if (d.numberOfDays !== undefined) setNumberOfDays(d.numberOfDays);
    if (d.fromTime !== undefined) setFromTime(d.fromTime);
    if (d.toTime !== undefined) setToTime(d.toTime);
    if (d.slotDuration !== undefined) setSlotDuration(d.slotDuration);
    if (d.prayerType !== undefined) setPrayerType(d.prayerType);
    if (d.talkItOver !== undefined) setTalkItOver(d.talkItOver !== false && d.talkItOver !== 'no');
    if (d.prayerFrequency !== undefined) setPrayerFrequency(d.prayerFrequency);
    if (d.daysOfWeek !== undefined) setDaysOfWeek(d.daysOfWeek);
    if (d.selectedTemplateId !== undefined) setSelectedTemplateId(d.selectedTemplateId);
    if (d.dayWiseData !== undefined && Object.keys(d.dayWiseData || {}).length > 0) {
      setDayWiseData(d.dayWiseData);
    }
    setActiveDraftId(draft.id);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };


  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('special-prayer', draftId, currentOrgKey);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };


  const handleCustomTemplateUpload = async (e) => {
    const file = e.target.files[0];
    console.log('Selected custom template file:', file);

    // setCustomTemplateTitle("");
    // setCustomTemplateFile(null);
    // setCustomTemplatePreview("");

    if (!file) return;

    // 1️⃣ File size check
    if (file.size > 200 * 1024) {
      alert("Please upload an image smaller than 200KB.");

      setCustomTemplateFile(null);
      setCustomTemplatePreview("");
      return;
    }

    // 2️⃣ Banner dimension check (wide, not tall)
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const aspect = img.width / img.height;

      // Accept if width is at least 1.5 times height (banner layout)
      if (aspect < 1.5) {
        alert("Please upload a wide banner-style image (example: 1200×600).");
        setCustomTemplateFile(null);
        setCustomTemplatePreview("");
        return;
      }

      // Valid
      setCustomTemplateFile(file);
      setCustomTemplatePreview(img.src);
    };
  };







  // Auto calculate days
  useEffect(() => {
    let totalDays = 0;
    if (prayerPlan === "fixed" || prayerPlan === "open") {
      if (fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        if (to >= from) {
          totalDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
        }
      }
    } else if (prayerPlan === "floating") {
      totalDays = numberOfDays && Number(numberOfDays) > 0 ? Number(numberOfDays) : 0;
    } else if (prayerPlan === "chain") {
      if (fromDate && toDate) {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        if (to >= from) {
          totalDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
        } else {
          totalDays = 1;
        }
      } else if (numberOfDays && Number(numberOfDays) > 0) {
        totalDays = Number(numberOfDays);
      } else {
        totalDays = 1;
      }
    }

    setNumDays(totalDays);

    if (totalDays > 0) {
      setDayWiseData((prev) => {
        const newData = {};
        const baseDay1 = prev[1] || {
          title: "", description: "",
          te_title: "", te_description: "",
          ta_title: "", ta_description: "",
          ml_title: "", ml_description: "",
          hi_title: "", hi_description: "",
          currentLang: "en",
          prayerPoints: []
        };
        for (let i = 1; i <= totalDays; i++) {
          if (sameContentForAllDays && i > 1) {
            newData[i] = JSON.parse(JSON.stringify(baseDay1));
            if (baseDay1.image) newData[i].image = baseDay1.image;
          } else {
            newData[i] = prev[i] || {
              title: "", description: "",
              te_title: "", te_description: "",
              ta_title: "", ta_description: "",
              ml_title: "", ml_description: "",
              hi_title: "", hi_description: "",
              currentLang: "en",
              prayerPoints: []
            };
          }
        }
        return newData;
      });

      setCurrentDay((prev) => {
        if (!prev || prev < 1 || prev > totalDays) {
          return 1;
        }
        return prev;
      });
    }
  }, [fromDate, toDate, numberOfDays, prayerPlan, sameContentForAllDays]);

  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [previewModalMode, setPreviewModalMode] = useState('preview'); // 'example' | 'preview'
  const [reviewSuccessModalOpen, setReviewSuccessModalOpen] = useState(false);
  const [savedAsPublished, setSavedAsPublished] = useState(false); // true if Save & Publish Now was used


  const handleOpenDay = (day) => setCurrentDay(day);

  // 2.2 Improve "Same Prayer for All Days" Option with Day 1 Validation & Informational Alert
  const handleToggleSameContent = (checked) => {
    if (checked) {
      const day1 = dayWiseData[1];
      const hasTitle = day1?.title && day1.title.trim() !== "";
      const hasDesc = day1?.description && day1.description.trim() !== "";
      const hasPoints = day1?.prayerPoints && day1.prayerPoints.length > 0 && day1.prayerPoints.some(p => (p.description && p.description.trim() !== "") || (p.title && p.title.trim() !== ""));

      if (!hasTitle && !hasDesc && !hasPoints) {
        setToast({
          open: true,
          message: "Please enter content for Day 1 first before enabling 'Same prayer for all days'.",
          severity: "warning"
        });
        setSameContentForAllDays(false);
        return;
      }

      setSameContentForAllDays(true);
      if (numDays > 1 && day1) {
        setDayWiseData((prev) => {
          const updated = { ...prev };
          for (let d = 2; d <= numDays; d++) {
            updated[d] = JSON.parse(JSON.stringify(day1));
            if (day1.image) updated[d].image = day1.image;
          }
          return updated;
        });
        setToast({
          open: true,
          message: `Day 1 content synced across all ${numDays} days! Any edits will now sync to all days.`,
          severity: "info"
        });
      }
    } else {
      setSameContentForAllDays(false);
      setToast({
        open: true,
        message: "'Same prayer for all days' disabled. Each day can now have customized prayers.",
        severity: "info"
      });
    }
  };

  // 2.4 Dynamically generate prayer points based on point count
  const handleSetPrayerPointsCount = (count) => {
    if (!currentDay) return;
    const targetCount = Math.max(0, parseInt(count, 10) || 0);

    setDayWiseData((prev) => {
      const updateDayPoints = (dayObj) => {
        const existing = [...((dayObj && dayObj.prayerPoints) || [])];
        if (targetCount > existing.length) {
          const diff = targetCount - existing.length;
          for (let i = 0; i < diff; i++) {
            existing.push({
              title: "",
              description: "",
              te_title: "",
              te_description: "",
              ta_title: "",
              ta_description: "",
              ml_title: "",
              ml_description: "",
              hi_title: "",
              hi_description: "",
              currentLang: "en"
            });
          }
        } else if (targetCount < existing.length) {
          existing.length = targetCount;
        }
        return existing;
      };

      if (sameContentForAllDays) {
        const updated = { ...prev };
        for (let d = 1; d <= numDays; d++) {
          updated[d] = {
            ...(prev[d] || {}),
            prayerPoints: updateDayPoints(prev[d])
          };
        }
        return updated;
      }

      return {
        ...prev,
        [currentDay]: {
          ...prev[currentDay],
          prayerPoints: updateDayPoints(prev[currentDay])
        }
      };
    });
  };

  const handleInputChange = (field, value) => {
    if (!currentDay) return;
    setDayWiseData((prev) => {
      if (sameContentForAllDays) {
        const updated = { ...prev };
        for (let d = 1; d <= numDays; d++) {
          updated[d] = { ...(prev[d] || {}), [field]: value };
        }
        return updated;
      }
      return {
        ...prev,
        [currentDay]: { ...prev[currentDay], [field]: value },
      };
    });
  };

  const addPrayerPoint = () => {
    if (!currentDay) return;
    const newPoint = {
      title: "", description: "",
      te_title: "", te_description: "",
      ta_title: "", ta_description: "",
      ml_title: "", ml_description: "",
      hi_title: "", hi_description: "",
      currentLang: "en"
    };
    setDayWiseData((prev) => {
      if (sameContentForAllDays) {
        const updated = { ...prev };
        for (let d = 1; d <= numDays; d++) {
          updated[d] = {
            ...(prev[d] || {}),
            prayerPoints: [...((prev[d] && prev[d].prayerPoints) || []), JSON.parse(JSON.stringify(newPoint))],
          };
        }
        return updated;
      }
      return {
        ...prev,
        [currentDay]: {
          ...prev[currentDay],
          prayerPoints: [...(prev[currentDay].prayerPoints || []), newPoint],
        },
      };
    });
  };

  const handleDailyPrayerPointChange = (index, field, value) => {
    setDayWiseData((prev) => {
      if (sameContentForAllDays) {
        const updated = { ...prev };
        for (let d = 1; d <= numDays; d++) {
          const points = [...((prev[d] && prev[d].prayerPoints) || [])];
          if (points[index]) {
            points[index] = { ...points[index], [field]: value };
            updated[d] = { ...(prev[d] || {}), prayerPoints: points };
          }
        }
        return updated;
      }
      const updatedPoints = [...(prev[currentDay]?.prayerPoints || [])];
      if (updatedPoints[index]) {
        updatedPoints[index] = { ...updatedPoints[index], [field]: value };
      }
      return {
        ...prev,
        [currentDay]: { ...prev[currentDay], prayerPoints: updatedPoints },
      };
    });
  };

  const removePrayerPoint = (index) => {
    setDayWiseData((prev) => {
      if (sameContentForAllDays) {
        const updated = { ...prev };
        for (let d = 1; d <= numDays; d++) {
          const points = [...((prev[d] && prev[d].prayerPoints) || [])];
          points.splice(index, 1);
          updated[d] = { ...(prev[d] || {}), prayerPoints: points };
        }
        return updated;
      }
      const updatedPoints = [...(prev[currentDay]?.prayerPoints || [])];
      updatedPoints.splice(index, 1);
      return {
        ...prev,
        [currentDay]: { ...prev[currentDay], prayerPoints: updatedPoints },
      };
    });
  };

  // Quick Action: Apply Day 1's title, description, and points to all other days
  const handleApplyDay1ToAll = () => {
    const day1 = dayWiseData[1];
    if (!day1) {
      setToast({ open: true, message: "Please configure Day 1 first.", severity: "warning" });
      return;
    }
    const updated = { ...dayWiseData };
    for (let d = 2; d <= numDays; d++) {
      updated[d] = JSON.parse(JSON.stringify(day1));
    }
    setDayWiseData(updated);
    setToast({ open: true, message: `Day 1 content successfully applied to all ${numDays} days!`, severity: "success" });
  };

  // Quick Action: Copy current day's content to the next day
  const handleCopyCurrentToNext = () => {
    if (!currentDay || currentDay >= numDays) return;
    const current = dayWiseData[currentDay];
    const nextDay = currentDay + 1;
    setDayWiseData(prev => ({
      ...prev,
      [nextDay]: JSON.parse(JSON.stringify(current))
    }));
    setCurrentDay(nextDay);
    setToast({ open: true, message: `Day ${currentDay} copied to Day ${nextDay}!`, severity: "success" });
  };

  // Quick Action: Import day-wise prayer points from Excel file
  const handleImportPointsExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const imported = await parseDayPointsExcel(file);
      setDayWiseData(prev => ({ ...prev, ...imported }));
      if (imported[1]) setCurrentDay(1);
      setToast({ open: true, message: "Prayer points successfully imported from Excel!", severity: "success" });
    } catch (err) {
      console.error("Excel import error:", err);
      setToast({ open: true, message: err.message || "Failed to import points from Excel", severity: "error" });
    }
    e.target.value = null;
  };


  useEffect(() => {
    fetchCategories();
    fetchTemplates();
  }, [selectedOrg, isSwitched, effectiveOrgId]);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}roles-organizations`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.organizations) {
          setOrganizations(data.organizations);
        }
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };
    if (user?.role === 1 && !isSwitched) {
      fetchOrganizations();
    }
  }, [user, isSwitched, API_URL]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}template-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        // 🔑 Handle invalid token
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      const list = Array.isArray(data?.data) && data.data.length > 0 ? data.data : DEFAULT_FALLBACK_TEMPLATES;
      setTemplatesList(list);

      if (list.length > 0) {
        setSelectedTemplateId((prev) => prev || list[0]?.id || 1);
      }
    } catch (err) {
      console.warn('Failed to fetch templates:', err);
      setTemplatesList(DEFAULT_FALLBACK_TEMPLATES);
    }
  };


  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}special-prayer-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      if (data?.data?.categories) {
        setCategories(data.data.categories);
      }
    } catch (err) {
      console.error("Error fetching special prayer categories:", err);
    }
  };

  // Handle template delete button click
  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  // Handle template deletion confirmation
  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}delete-template`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: templateToDelete.id
        })
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }

        setToast({
          open: true,
          message: data.message || 'Failed to delete template',
          severity: 'error'
        });
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
        return;
      }

      // Success
      setToast({
        open: true,
        message: data.message || 'Template deleted successfully',
        severity: 'success'
      });

      // Refresh templates list
      await fetchTemplates();

      // If deleted template was selected, select first template
      if (selectedTemplateId === templateToDelete.id && (templatesList || []).length > 1) {
        setSelectedTemplateId((templatesList || []).find(t => t.id !== templateToDelete.id)?.id);
      }

      setDeleteDialogOpen(false);
      setTemplateToDelete(null);

    } catch (error) {
      console.error('Error deleting template:', error);
      setToast({
        open: true,
        message: 'Network error occurred',
        severity: 'error'
      });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  // Check if user can delete a template
  const canDeleteTemplate = (template) => {
    if (!user) return false;

    //console.log(user)

    // Super admin (role 1) can only delete templates with org_id null
    if (user.role === 1) {
      return template.org_id === null || template.org_id === undefined;
    }

    //console.log(template.org_id)

    // Other roles can only delete their own organization's templates
    return template.org_id === user.org_id;
  };

  const [timeError, setTimeError] = useState("");

  const handleTimeChange = (field, value) => {
    if (field === "from") setFromTime(value);
    if (field === "to") setToTime(value);

    const start = field === "from" ? value : fromTime;
    const end = field === "to" ? value : toTime;

    // Only validate when both have values
    if (start && end && end <= start) {
      setTimeError("To Time must be later than From Time");
    } else {
      setTimeError("");
    }
  };



  const handleAddDevotion = async (publishImmediately = false) => {
    const token = localStorage.getItem('token');

    // ── Validation: require title for drafts; require all fields if publishing immediately
    if (!title || !title.trim()) {
      setToast({
        open: true,
        message: "Please enter at least a Prayer Title before saving.",
        severity: "warning"
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (publishImmediately) {
      const cleanTitle = title ? String(title).trim() : '';
      const cleanDesc = description ? description.replace(/<[^>]+>/g, '').trim() : '';
      if (!cleanTitle || !category || !cleanDesc || !fromTime || !toTime || !slotDuration) {
        setToast({ open: true, message: "To publish, Category, Title, Description, From Time, To Time and Slot duration are required.", severity: "error" });
        return false;
      }
      if (prayerPlan === 'fixed' || prayerPlan === 'open') {
        if (!fromDate || !toDate) {
          setToast({ open: true, message: "From Date and To Date are required.", severity: "error" });
          return false;
        } else if (new Date(toDate) <= new Date(fromDate)) {
          setToast({ open: true, message: "To Date must be later than From Date", severity: "error" });
          return false;
        }
      }
      if (prayerPlan === 'floating') {
        if (!numberOfDays || Number(numberOfDays) < 1) {
          setToast({ open: true, message: "Number of days is required.", severity: "error" });
          return false;
        }
      }

      // Verify at least one prayer point exists before publishing
      let hasPoints = false;
      if (dayWiseData && Object.keys(dayWiseData).length > 0) {
        for (const dayKey in dayWiseData) {
          const dayD = dayWiseData[dayKey];
          if (Array.isArray(dayD.prayerPoints) && dayD.prayerPoints.some(pt => {
            const t = pt.title ? String(pt.title).trim() : '';
            const d = (pt.description || pt.english || pt.prayer_point) ? String(pt.description || pt.english || pt.prayer_point).trim() : '';
            return t !== '' || d !== '';
          })) {
            hasPoints = true;
            break;
          }
        }
      }
      if (!hasPoints) {
        setToast({ open: true, message: "To publish, at least one prayer point is required.", severity: "error" });
        return false;
      }
    }

    // Clear any old form errors since we're allowing partial saves
    setFormErrors({});

    // Filter out blank prayer points (allow saving with partial/empty points)
    const cleanedDayWiseData = {};
    if (dayWiseData && Object.keys(dayWiseData).length > 0) {
      for (const dayKey in dayWiseData) {
        const dayD = dayWiseData[dayKey];
        const cleanedPoints = (dayD.prayerPoints || []).filter(pt => {
          const t = pt.title ? String(pt.title).trim() : '';
          const d = (pt.description || pt.english) ? String(pt.description || pt.english).trim() : '';
          return t !== '' || d !== ''; // keep only non-empty points
        });
        cleanedDayWiseData[dayKey] = { ...dayD, prayerPoints: cleanedPoints };
      }
    }
    // for (var i = 0; i < prayerPoints.length; i++) {
    //   if (prayerPoints[i]['title'] == '' || prayerPoints[i]['english'] == '') {
    //     alert('Prayer Point' + (i + 1) + ' ' + 'Title and English Prayer Point is required');
    //     return false;
    //   }
    // }

    const formData = new FormData();
    formData.append('customTemplateTitle', customTemplateTitle || '');
    formData.append('customTemplateFile', customTemplateFile || '');

    formData.append('title', title || '');
    formData.append('category', category || '');
    formData.append('description', description || '');
    formData.append('prayerPlan', prayerPlan || 'fixed');
    formData.append('duration_hours', prayerPlan === 'chain' ? chainDurationHours : '');
    formData.append('fromDate', prayerPlan === 'floating' ? '' : (fromDate || ''));
    formData.append('toDate', prayerPlan === 'floating' ? '' : (prayerPlan === 'chain' ? (toDate || fromDate || '') : (toDate || '')));
    formData.append('prayerType', prayerPlan === 'chain' ? 'chain' : (prayerType || 'Group'));

    formData.append('fromTime', fromTime || '00:00');
    formData.append('toTime', toTime || '23:59');
    formData.append('numberOfDays', prayerPlan === 'floating' ? (numberOfDays || '1') : (prayerPlan === 'chain' ? (numDays ? String(numDays) : '1') : (numDays || '1')));
    formData.append('slotDuration', slotDuration || '30');

    formData.append('prayerFrequency', prayerFrequency || 'Daily');
    formData.append('talk_it_over', talkItOver ? 'yes' : 'no');
    formData.append('daysOfWeek', JSON.stringify(daysOfWeek || []));
    formData.append('selectedTemplateId', (templatesList || []).find(t => t.id === selectedTemplateId)?.url || '');
    formData.append('tempId', selectedTemplateId || '');

    // Always save as inactive (pending review) — admin publishes later
    formData.append('status', publishImmediately ? 'active' : 'inactive');

    let finalDayWiseData = { ...cleanedDayWiseData };
    if (sameContentForAllDays && numDays > 1 && finalDayWiseData[1]) {
      for (let d = 2; d <= numDays; d++) {
        finalDayWiseData[d] = JSON.parse(JSON.stringify(finalDayWiseData[1]));
        if (finalDayWiseData[1].image) {
          finalDayWiseData[d].image = finalDayWiseData[1].image;
        }
      }
    }
    // If no dayWiseData at all, create a minimal placeholder for day 1
    if (!finalDayWiseData || Object.keys(finalDayWiseData).length === 0) {
      finalDayWiseData = {
        1: { title: title || '', description: description || '', prayerPoints: [] }
      };
    }
    formData.append('dayWiseData', JSON.stringify(finalDayWiseData));

    const targetOrg = isSwitched
      ? (effectiveOrgId || user?.org_id)
      : (user?.role === 1 ? (selectedOrg !== 'global' ? selectedOrg : null) : user?.org_id);

    if (targetOrg && targetOrg !== 'global') {
      formData.append('organization', targetOrg);
      formData.append('organization_id', targetOrg);
    } else if (user?.role === 1 && !isSwitched) {
      formData.append('organization', selectedOrg || 'global');
    }

    // Append files for Day-wise content
    Object.keys(finalDayWiseData).forEach((day) => {
      const dayData = finalDayWiseData[day];
      if (dayData && dayData.image instanceof File) {
        formData.append(`day_${day}_image`, dayData.image);
      }
      if (dayData && dayData.prayerPoints) {
        dayData.prayerPoints.forEach((point, index) => {
          if (point.image instanceof File) {
            formData.append(`day_${day}_point_${index}_image`, point.image);
          }
        });
      }
    });

    try {
      const response = await fetch(`${API_URL}add-special-prayer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(targetOrg && targetOrg !== 'global' ? { 'x-org-id': String(targetOrg) } : {})
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        setToast({ open: true, message: "Save failed: " + (data?.message || data?.error || 'Unknown error'), severity: "error" });
        return;
      }

      if (response.ok) {
        isSubmittedRef.current = true;
        setSavedAsPublished(publishImmediately);
        if (activeDraftId) {
          DraftManager.deleteDraft('special-prayer', activeDraftId, currentOrgKey);
        }
        setReviewSuccessModalOpen(true);
      }
    } catch (err) {
      console.error('Error saving special prayer:', err);
      setToast({ open: true, message: "Network error. Please try again.", severity: "error" });
    }
  };



  const translateText = async (text, targetLang) => {
    if (!text) return "";
    try {
      const response = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`);
      const data = await response.json();
      return data[0].map(x => x[0]).join('');
    } catch (error) {
      console.error("Translation failed", error);
      return text; // Fallback
    }
  };

  const handleTranslatePrayerPoint = async (index, targetLang) => {
    const item = dayWiseData[currentDay]?.prayerPoints[index];
    if (!item) return;

    const title = item.title;
    const desc = item.description;

    if (!title && !desc) {
      setToast({ open: true, message: "Please enter English content first", severity: "warning" });
      return;
    }

    const translatedTitle = await translateText(title, targetLang);
    const translatedDesc = await translateText(desc, targetLang);

    setDayWiseData((prev) => {
      const updatedPoints = [...(prev[currentDay]?.prayerPoints || [])];
      if (updatedPoints[index]) {
        updatedPoints[index][`${targetLang}_title`] = translatedTitle;
        updatedPoints[index][`${targetLang}_description`] = translatedDesc;
        updatedPoints[index].currentLang = targetLang;
      }
      return {
        ...prev,
        [currentDay]: { ...prev[currentDay], prayerPoints: updatedPoints }
      };
    });
  };

  
  const getDayTitleField = () => {
    if (activeContentLang === 'te') return 'te_title';
    if (activeContentLang === 'ta') return 'ta_title';
    if (activeContentLang === 'ml') return 'ml_title';
    if (activeContentLang === 'hi') return 'hi_title';
    return 'title';
  };
  const getDayDescField = () => {
    if (activeContentLang === 'te') return 'te_description';
    if (activeContentLang === 'ta') return 'ta_description';
    if (activeContentLang === 'ml') return 'ml_description';
    if (activeContentLang === 'hi') return 'hi_description';
    return 'description';
  };
  const getPointTitleField = () => {
    if (activeContentLang === 'te') return 'te_title';
    if (activeContentLang === 'ta') return 'ta_title';
    if (activeContentLang === 'ml') return 'ml_title';
    if (activeContentLang === 'hi') return 'hi_title';
    return 'title';
  };
  const getPointDescField = () => {
    if (activeContentLang === 'te') return 'te_description';
    if (activeContentLang === 'ta') return 'ta_description';
    if (activeContentLang === 'ml') return 'ml_description';
    if (activeContentLang === 'hi') return 'hi_description';
    return 'description';
  };
  const getActiveLangLabel = () => {
    switch (activeContentLang) {
      case 'te': return 'Telugu (తెలుగు)';
      case 'ta': return 'Tamil (தமிழ்)';
      case 'ml': return 'Malayalam (മലയാളം)';
      case 'hi': return 'Hindi (हिंदी)';
      default: return 'English';
    }
  };

  const getThemesForCurrentDay = () => {
    const rawThemes = dayWiseData[currentDay]?.prayerThemes;
    if (Array.isArray(rawThemes) && rawThemes.length > 0) {
      return rawThemes;
    }
    // Return Theme 1 matching dayWiseData[currentDay]
    return [{
      title: dayWiseData[currentDay]?.[getDayTitleField()] || dayWiseData[currentDay]?.title || "",
      description: dayWiseData[currentDay]?.[getDayDescField()] || dayWiseData[currentDay]?.description || "",
      [getDayTitleField()]: dayWiseData[currentDay]?.[getDayTitleField()] || "",
      [getDayDescField()]: dayWiseData[currentDay]?.[getDayDescField()] || "",
    }];
  };

  const handleThemeChange = (index, field, value) => {
    const currentThemes = [...getThemesForCurrentDay()];
    if (!currentThemes[index]) {
      currentThemes[index] = {};
    }
    currentThemes[index] = {
      ...currentThemes[index],
      [field]: value,
    };
    if (field === getDayTitleField() || field === 'title') {
      currentThemes[index].title = value;
    }
    if (field === getDayDescField() || field === 'description') {
      currentThemes[index].description = value;
    }

    setDayWiseData((prev) => {
      const updatedDay = {
        ...(prev[currentDay] || {}),
        prayerThemes: currentThemes,
      };
      // Keep Day's root title & description in sync with Theme 1
      if (index === 0) {
        updatedDay[field] = value;
        if (field === getDayTitleField() || field === 'title') {
          updatedDay.title = value;
        }
        if (field === getDayDescField() || field === 'description') {
          updatedDay.description = value;
        }
      }
      return {
        ...prev,
        [currentDay]: updatedDay,
      };
    });
  };

  const handleAddTheme = () => {
    const currentThemes = [...getThemesForCurrentDay()];
    currentThemes.push({
      title: "",
      description: "",
      [getDayTitleField()]: "",
      [getDayDescField()]: "",
    });
    setDayWiseData((prev) => ({
      ...prev,
      [currentDay]: {
        ...(prev[currentDay] || {}),
        prayerThemes: currentThemes,
      }
    }));
  };

  const handleRemoveTheme = (index) => {
    if (index === 0) return;
    const currentThemes = getThemesForCurrentDay().filter((_, i) => i !== index);
    setDayWiseData((prev) => ({
      ...prev,
      [currentDay]: {
        ...(prev[currentDay] || {}),
        prayerThemes: currentThemes,
      }
    }));
  };

  const handleTranslateAllForDay = async (targetLang) => {
    if (!currentDay) return;
    const dayData = dayWiseData[currentDay];
    const title = dayData?.title || "";
    const desc = dayData?.description || "";
    const points = dayData?.prayerPoints || [];

    if (!title && !desc && points.length === 0) {
      setToast({ open: true, message: "Please enter English content first before translating", severity: "warning" });
      return;
    }

    setToast({ open: true, message: `Translating Day ${currentDay} devotional & all prayer points...`, severity: "info" });

    const translatedDayTitle = title ? await translateText(title, targetLang) : "";
    const translatedDayDesc = desc ? await translateText(desc, targetLang) : "";

    const updatedPoints = await Promise.all(
      points.map(async (pt) => {
        const ptTitle = pt.title ? await translateText(pt.title, targetLang) : "";
        const ptDesc = pt.description ? await translateText(pt.description, targetLang) : "";
        return {
          ...pt,
          [`${targetLang}_title`]: ptTitle,
          [`${targetLang}_description`]: ptDesc,
        };
      })
    );

    setDayWiseData((prev) => {
      const updated = {
        ...prev,
        [currentDay]: {
          ...prev[currentDay],
          [`${targetLang}_title`]: translatedDayTitle,
          [`${targetLang}_description`]: translatedDayDesc,
          prayerPoints: updatedPoints,
        }
      };

      if (sameContentForAllDays && numDays > 1) {
        for (let d = 1; d <= numDays; d++) {
          updated[d] = {
            ...(updated[d] || {}),
            [`${targetLang}_title`]: translatedDayTitle,
            [`${targetLang}_description`]: translatedDayDesc,
            prayerPoints: JSON.parse(JSON.stringify(updatedPoints)),
          };
        }
      }

      return updated;
    });

    setActiveContentLang(targetLang);
    setToast({ open: true, message: `Day ${currentDay} devotional and all prayer points translated successfully!`, severity: "success" });
  };

  const handleTranslateDayContent = async (targetLang) => {
    const title = dayWiseData[currentDay]?.title;
    const desc = dayWiseData[currentDay]?.description;

    if (!title && !desc) {
      setToast({ open: true, message: "Please enter English content first", severity: "warning" });
      return;
    }

    const translatedTitle = await translateText(title, targetLang);
    const translatedDesc = await translateText(desc, targetLang);

    setDayWiseData((prev) => ({
      ...prev,
      [currentDay]: {
        ...prev[currentDay],
        [`${targetLang}_title`]: translatedTitle,
        [`${targetLang}_description`]: translatedDesc,
        currentLang: targetLang
      }
    }));
  };

  const validateForm = () => {
    let errors = {};

    // ---------------- MAIN FIELDS ----------------

    if (!title.trim()) {
      errors.title = "Title is required";
    }

    if (!category) {
      errors.category = "Category is required";
    }

    if (!description || description.trim() === "" || description === "<p><br></p>") {
      errors.description = "Description is required";
    }

    if (!prayerPlan) {
      errors.prayerPlan = "Prayer Plan is required";
    }

    if (prayerPlan === "chain") {
      if (!fromDate) errors.fromDate = "Start date is required";
      if (!fromTime) errors.fromTime = "Start time is required";
      const hrs = parseFloat(chainDurationHours);
      if (isNaN(hrs) || hrs <= 0 || hrs > 24) {
        errors.chainDurationHours = "Chain prayer duration must be between 1 and 24 hours";
      }
      const slot = parseInt(slotDuration, 10);
      if (isNaN(slot) || slot <= 0) {
        errors.slotDuration = "Slot duration is required";
      } else if (Math.round(hrs * 60) % slot !== 0) {
        errors.slotDuration = "Total duration must be divisible by slot duration";
      }
    } else if (prayerPlan === "fixed" || prayerPlan === "open") {
      if (!fromDate) errors.fromDate = "From date is required";
      if (!toDate) errors.toDate = "To date is required";

      if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
        errors.toDate = "To Date cannot be earlier than From Date";
      }
    }

    if (prayerPlan === "floating") {
      if (!numberOfDays || Number(numberOfDays) <= 0) {
        errors.numberOfDays = "Number of days must be a valid number";
      }
    }

    // ------------- TIME VALIDATIONS ----------------
    if (prayerPlan !== "chain") {
      if (!fromTime) errors.fromTime = "From time is required";
      if (!toTime) errors.toTime = "To time is required";

      if (fromTime && toTime && fromTime >= toTime) {
        errors.toTime = "To time must be later than From time";
      }

      if (!slotDuration) {
        errors.slotDuration = "Slot duration is required";
      }
    }

    // ------------- PRAYER TYPE ----------------
    if (!prayerType && prayerPlan !== "chain") {
      errors.prayerType = "Prayer type is required";
    }

    if (!prayerFrequency && prayerPlan !== "chain") {
      errors.prayerFrequency = "Prayer frequency is required";
    }

    if (prayerFrequency === "Weekly" && daysOfWeek.length === 0 && prayerPlan !== "chain") {
      errors.daysOfWeek = "Select at least one day";
    }

    // ------------- CONTENT TYPE ----------------
    if (!contentType && prayerPlan !== "chain") {
      errors.contentType = "Choose if daily content is required";
    }

    const isAnyDayFilled = dayWiseData && Object.values(dayWiseData).some(day => {
      const hasTitle = day.title && day.title.trim() !== "";
      const hasDescription = day.description && day.description.trim() !== "";
      const hasPrayerPoints = day.prayerPoints && day.prayerPoints.length > 0;
      return hasTitle || hasDescription || hasPrayerPoints;
    });

    if (!isAnyDayFilled) {
      if (prayerPlan === "chain") {
        dayWiseData[1] = {
          title: title || "Chain Prayer Intercession",
          description: description || "Continuous 24-Hour Chain Prayer Relay",
          prayerPoints: []
        };
      } else {
        errors.content = "Please add prayer content for at least one day.";
      }
    }


    return errors;
  };



  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3.5 }, boxSizing: "border-box" }}>
      {/* ── Top Page Header (Standard Admin Page Header) ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.2} mb={0.8} flexWrap="wrap">
            <Button
              size="small"
              startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
              onClick={handleClose}
              sx={{
                color: isLight ? "#0F766E" : "#64D2E2",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
                p: 0,
                minWidth: 0,
                "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
              }}
            >
              Back to Prayer Closet
            </Button>
            {!isWizardActive && (
              <>
                <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.4)" }}>
                  •
                </Typography>
                <Button
                  size="small"
                  startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setIsWizardActive(true)}
                  sx={{
                    color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    p: 0,
                    minWidth: 0,
                    "&:hover": { bgcolor: "transparent", textDecoration: "underline", color: isLight ? "#0F766E" : "#64D2E2" },
                  }}
                >
                  Edit Schedule (Step 1)
                </Button>
              </>
            )}
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: isLight ? "#0F172A" : "#FFFFFF",
              letterSpacing: -0.3,
            }}
          >
            Create Special Prayer
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.65)",
              mt: 0.3,
            }}
          >
            {isWizardActive
              ? "Answer 3 quick questions to set up your prayer format & cadence"
              : "Review your configuration and complete the details below"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={isWizardActive ? undefined : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            onClick={() => setIsWizardActive((prev) => !prev)}
            sx={{
              color: isLight ? "#0F766E" : "#64D2E2",
              borderColor: isLight ? "#0F766E" : "rgba(100, 210, 226, 0.5)",
              bgcolor: isLight ? "rgba(15, 118, 110, 0.06)" : "rgba(255, 255, 255, 0.05)",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              py: 0.7,
              px: 1.5,
              fontSize: "0.82rem",
              "&:hover": {
                bgcolor: isLight ? "rgba(15, 118, 110, 0.12)" : "rgba(255, 255, 255, 0.12)",
              },
            }}
          >
            {isWizardActive ? "Direct Form Mode →" : "Step-by-Step Setup"}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={handleManualSaveDraft}
            sx={{
              color: isLight ? "#0F766E" : "#64D2E2",
              borderColor: isLight ? "rgba(15, 118, 110, 0.4)" : "rgba(76, 161, 175, 0.4)",
              bgcolor: isLight ? "rgba(15, 118, 110, 0.05)" : "rgba(76, 161, 175, 0.1)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              py: 0.7,
              px: 1.5,
              fontSize: "0.82rem",
              "&:hover": {
                bgcolor: isLight ? "rgba(15, 118, 110, 0.12)" : "rgba(76, 161, 175, 0.25)",
              },
            }}
          >
            Save Draft
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<DraftsIcon sx={{ fontSize: 17 }} />}
            onClick={() => {
              setDraftsList(DraftManager.getDrafts("special-prayer", currentOrgKey));
              setDraftsOpen(true);
            }}
            sx={{
              bgcolor: isLight ? "#0F766E" : "rgba(76, 161, 175, 0.35)",
              border: isLight ? "none" : "1px solid rgba(76, 161, 175, 0.6)",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              py: 0.7,
              px: 1.6,
              fontSize: "0.82rem",
              "&:hover": {
                bgcolor: isLight ? "#115E59" : "rgba(76, 161, 175, 0.55)",
              },
            }}
          >
            Drafts ({draftsList.length}/5)
          </Button>

          <Tooltip title="Prayer Closet Settings & Language" placement="bottom">
            <IconButton
              onClick={() => setSettingsModalOpen(true)}
              sx={{
                color: isLight ? "#0D9488" : "#64d2e2",
                bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.15)",
                border: isLight ? "1.5px solid rgba(13, 148, 136, 0.35)" : "1px solid rgba(76, 161, 175, 0.35)",
                borderRadius: "8px",
                p: 0.75,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.18)" : "rgba(76, 161, 175, 0.3)",
                  borderColor: isLight ? "#0D9488" : "#4CA1AF",
                  transform: "rotate(30deg)",
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Close and return to Prayer Closet (auto-saves draft)" placement="bottom">
            <IconButton
              onClick={handleClose}
              aria-label="Close"
              sx={{
                color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.8)",
                bgcolor: isLight ? "rgba(15, 23, 42, 0.05)" : "rgba(255, 255, 255, 0.08)",
                border: isLight ? "1.5px solid rgba(100, 116, 139, 0.3)" : "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "8px",
                p: 0.75,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "rgba(239, 68, 68, 0.15)",
                  borderColor: "rgba(239, 68, 68, 0.4)",
                  color: "#EF4444",
                  transform: "scale(1.05)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isWizardActive ? (
        <Box
          sx={{
            width: "100%",
            borderRadius: 3,
            p: { xs: 2, md: 3.5 },
            bgcolor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.03)",
            border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: isLight ? "0 2px 8px rgba(15, 23, 42, 0.04)" : "none",
          }}
        >
          <PrayerWizardSetup
            prayerPlan={prayerPlan}
            onPrayerPlanChange={setPrayerPlan}
            prayerFrequency={prayerFrequency}
            onPrayerFrequencyChange={setPrayerFrequency}
            daysOfWeek={daysOfWeek}
            onDaysOfWeekChange={setDaysOfWeek}
            fromDate={fromDate}
            onFromDateChange={setFromDate}
            toDate={toDate}
            onToDateChange={setToDate}
            numberOfDays={numberOfDays}
            onNumberOfDaysChange={setNumberOfDays}
            slotDuration={slotDuration}
            onSlotDurationChange={setSlotDuration}
            fromTime={fromTime}
            onFromTimeChange={(val) => handleTimeChange("from", val)}
            toTime={toTime}
            onToTimeChange={(val) => handleTimeChange("to", val)}
            chainDurationHours={chainDurationHours}
            onChainDurationHoursChange={setChainDurationHours}
            onComplete={() => {
              window.history.pushState({ view: 'prayer-form' }, '');
              setIsWizardActive(false);
              setToast({
                open: true,
                message: "Setup answers applied! Review and fill in remaining details below.",
                severity: "success",
              });
            }}
            onSkip={() => setIsWizardActive(false)}
            isLight={isLight}
          />
        </Box>
      ) : (
        <>
            {/* Compact Configured Schedule Bar */}
            <Box
              sx={{
                mx: { xs: 2, md: 4 },
                mt: 2.5,
                mb: 1,
                p: 1.5,
                px: 2.5,
                borderRadius: '12px',
                background: 'rgba(13, 148, 136, 0.08)',
                border: '1px solid rgba(13, 148, 136, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Box display="flex" alignItems="center">
                  <Typography variant="caption" sx={{ color: '#0F766E', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Schedule Setup:
                  </Typography>
                  <HelpTooltip
                    title="Schedule Configuration"
                    purpose="Overview of prayer plan, cadence, and slot settings established in the wizard."
                    howItWorks="Click 'Change Setup' to reopen the interactive wizard and reconfigure any setting."
                    mobileEffect="Dictates how sessions appear, how countdowns run, and how slots are allocated in the mobile app."
                  />
                </Box>
                <Chip
                  size="small"
                  label={`Type: ${prayerPlan === 'floating' ? 'Floating' : prayerPlan === 'open' ? 'Open' : prayerPlan === 'chain' ? 'Chain Prayer' : 'Fixed'}`}
                  sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                />
                <Chip
                  size="small"
                  label={`Frequency: ${prayerPlan === 'chain' ? 'Continuous Relay' : prayerFrequency}`}
                  sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                />
                {prayerPlan === 'chain' && (
                  <Chip
                    size="small"
                    label={`Duration: ${chainDurationHours}h (Max 24h)`}
                    sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                  />
                )}
                {prayerPlan === 'chain' && fromDate && (
                  <Chip
                    size="small"
                    label={`Start: ${fromDate} ${fromTime || '00:00'}`}
                    sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                  />
                )}
                {prayerPlan === 'floating' && numberOfDays && (
                  <Chip
                    size="small"
                    label={`Duration: ${numberOfDays} Days`}
                    sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                  />
                )}
                {(prayerPlan === 'fixed' || prayerPlan === 'open') && fromDate && toDate && (
                  <Chip
                    size="small"
                    label={`Dates: ${fromDate} to ${toDate}`}
                    sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                  />
                )}
                {slotDuration && (
                  <Chip
                    size="small"
                    label={`Slot: ${slotDuration}m`}
                    sx={{ bgcolor: '#FFFFFF', color: '#0F766E', fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(13, 148, 136, 0.2)' }}
                  />
                )}
              </Box>

              <Button
                size="small"
                onClick={() => setIsWizardActive(true)}
                sx={{
                  color: '#0D9488',
                  bgcolor: '#FFFFFF',
                  textTransform: 'none',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  borderRadius: '8px',
                  border: '1px solid rgba(13, 148, 136, 0.3)',
                  px: 1.6,
                  py: 0.5,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.08)', borderColor: '#0D9488' },
                }}
              >
                Change Setup ✎
              </Button>
            </Box>

            <Box sx={{ p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Basic Info Section */}
          <Accordion defaultExpanded sx={{ border: '1px solid  #256974ff', borderRadius: '12px !important', boxShadow: 'none', '&:before': { display: 'none' }, overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ background: '#256974ff', borderBottom: '1px solid #256974ff' }}>
              <Box display="flex" alignItems="center">
                <Typography fontWeight={600} color="white">Basic Information</Typography>
                <HelpTooltip
                  title="Basic Information"
                  purpose="Essential metadata including title, classification, and format for this prayer altar."
                  howItWorks="Fill in these fields to publish the prayer campaign to registered church members."
                  mobileEffect="Forms the campaign header and banner displayed in the mobile app."
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {/* TOP: User Example & Talk It Over Option in One Line */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={2}
                mb={2.5}
                p={1.5}
                borderRadius={2}
                border="1px solid"
                borderColor={isLight ? 'rgba(13, 148, 136, 0.25)' : 'rgba(56, 189, 248, 0.25)'}
                bgcolor={isLight ? 'rgba(13, 148, 136, 0.04)' : 'rgba(56, 189, 248, 0.05)'}
              >
                {/* Left: User Example Button */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PhoneIphoneIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />}
                    onClick={() => setBasicInfoModal({ open: true, mode: 'example' })}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      px: 1.8,
                      height: 36,
                      boxShadow: '0 2px 8px rgba(15, 118, 110, 0.35)',
                      '&:hover': { background: 'linear-gradient(135deg, #115E59 0%, #0F766E 100%)' }
                    }}
                  >
                    User Example
                  </Button>
                  <HelpTooltip
                    title="User Example"
                    purpose="Interactive mobile app simulator showing how this prayer appears to users."
                    howItWorks="Renders a smartphone mockup matching the mobile app prayer screen."
                    mobileEffect="Provides a design reference for title, banner image, and description."
                  />
                </Box>

                {/* Right: Talk It Over Option */}
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isLight ? '#0F766E' : '#38BDF8' }}>
                      Talk It Over Option
                    </Typography>
                    <HelpTooltip
                      title="Talk It Over"
                      purpose="Enables intercessors to connect with a prayer partner to discuss insights and reflections during this prayer."
                      howItWorks="When selected, users see the Talk It Over card and can participate in partner reflection. Uncheck if you want to hide this option."
                      mobileEffect="Controls visibility of the Talk It Over feature in the mobile app for this prayer."
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={talkItOver}
                        onChange={(e) => setTalkItOver(e.target.checked)}
                        sx={{ color: isLight ? '#0D9488' : '#38BDF8', '&.Mui-checked': { color: isLight ? '#0D9488' : '#38BDF8' } }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600, color: talkItOver ? (isLight ? '#0F766E' : '#38BDF8') : '#94A3B8' }}>
                        {talkItOver ? "Enabled (Default)" : "Disabled"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between">
                {user?.role === 1 && !isSwitched && (
                  <Box flex="1 1 100%" mb={1}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: isLight ? '#475569' : '#CBD5E1' }}>
                        Select Organization
                      </Typography>
                      <HelpTooltip
                        title="Organization Scope"
                        purpose="Specifies whether this prayer belongs to a specific church branch or the global network."
                        howItWorks="Global prayers are visible to all users; church-specific prayers are targeted to church members."
                        mobileEffect="Renders church branding and displays under the church community tab on mobile."
                      />
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel>Select Organization</InputLabel>
                      <Select
                        value={selectedOrg}
                        label="Select Organization"
                        onChange={(e) => setSelectedOrg(e.target.value)}
                      >
                        <MenuItem value="global">
                          <em>Glocal Prayer Network (Global)</em>
                        </MenuItem>
                        {organizations.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.org_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                <Box flex="1 1 48%">
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isLight ? '#475569' : '#CBD5E1' }}>
                      Title *
                    </Typography>
                    <HelpTooltip
                      title="Prayer Campaign Title"
                      purpose="The primary name displayed on banners, cards, and notification banners."
                      howItWorks="Keep it concise and inspiring (e.g. '21 Days of Breakthrough & Fasting')."
                      mobileEffect="Displayed as the top title in prayer lists and on the home prayer card."
                    />
                  </Box>
                  <TextField label="Title" value={title}
                    fullWidth required
                    onChange={(e) => setTitle(e.target.value)}
                    error={Boolean(formErrors.title)}
                    helperText={formErrors.title}
                  />
                </Box>
                <Box flex="1 1 48%">
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isLight ? '#475569' : '#CBD5E1' }}>
                      Category *
                    </Typography>
                    <HelpTooltip
                      title="Prayer Category"
                      purpose="Categorizes the prayer initiative for effortless browsing and filtering."
                      howItWorks="Select the spiritual focus such as Missions, Healing, Revival, or Nation."
                      mobileEffect="Members can filter prayers by this category tag in the mobile explore feed."
                    />
                  </Box>
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>

                    {formErrors.category && (
                      <Typography color="error">{formErrors.category}</Typography>
                    )}
                  </FormControl>
                </Box>
                <Box flex="1 1 100%" mt={1} display="flex" alignItems="center" gap={3}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="subtitle2" sx={{ color: isLight ? '#1E293B' : '#FFFFFF', fontWeight: 600 }}>
                      Format *
                    </Typography>
                    <HelpTooltip
                      title="Prayer Format"
                      purpose="Choose between corporate congregational prayer (Group) or self-paced private prayer (Personal)."
                      howItWorks="Group prayers show fellow praying intercessors; Personal prayers are an individual devotion."
                      mobileEffect="Group prayers show intercessor count and avatars; Personal prayers track individual streaks."
                    />
                  </Box>
                  <RadioGroup
                    row
                    value={prayerType}
                    onChange={(e) => setPrayerType(e.target.value)}
                  >
                    <FormControlLabel
                      value="Group"
                      control={<Radio size="small" sx={{ color: isLight ? '#64748B' : '#94A3B8', '&.Mui-checked': { color: isLight ? '#0D9488' : '#4CA1AF' } }} />}
                      label={<Typography variant="body2" sx={{ color: isLight ? '#1E293B' : '#FFFFFF', fontWeight: 500 }}>Group Prayer</Typography>}
                    />
                    <FormControlLabel
                      value="Personal"
                      control={<Radio size="small" sx={{ color: isLight ? '#64748B' : '#94A3B8', '&.Mui-checked': { color: isLight ? '#0D9488' : '#4CA1AF' } }} />}
                      label={<Typography variant="body2" sx={{ color: isLight ? '#1E293B' : '#FFFFFF', fontWeight: 500 }}>Personal Prayer</Typography>}
                    />
                  </RadioGroup>
                </Box>

                <Box flex="1 1 100%" mt={2}>
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Description *
                    </Typography>
                    <HelpTooltip
                      title="Prayer Description"
                      purpose="Provides spiritual background, scripture verses, and instructions for intercessors."
                      howItWorks="Supports rich formatting with lists, highlights, and emphasis."
                      mobileEffect="Rendered in the expandable 'About this Prayer' sheet on mobile."
                    />
                  </Box>

                  <ReactQuill
                    theme="snow"
                    value={description}
                    onChange={(content) => setDescription(content)}
                  />

                  {formErrors.description && (
                    <Typography color="error" sx={{ mt: 1 }}>
                      {formErrors.description}
                    </Typography>
                  )}
                </Box>

                {/* ── Template Selection & Banner Image ── */}
                <Box flex="1 1 100%" mt={2.5}>
                  <Divider sx={{ mb: 2.5, borderColor: isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)' }} />
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF' }}>
                        Template Selection & Banner Image
                      </Typography>
                      <HelpTooltip
                        title="Prayer Banner & Template"
                        purpose="Select a visually compelling banner image that represents this prayer closet campaign."
                        howItWorks="Choose an existing curated template from our library or upload your own custom banner image."
                        mobileEffect="Displayed as the top banner image on the prayer screen in the mobile app."
                      />
                    </Box>

                    {/* Live Mobile Preview Button: Normal button, matching standard layout */}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<VisibilityIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />}
                      onClick={() => setBasicInfoModal({ open: true, mode: 'preview' })}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #256974 0%, #3B828E 100%)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        whiteSpace: 'nowrap',
                        px: 2,
                        height: 36,
                        boxShadow: '0 2px 8px rgba(37, 105, 116, 0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #1B5058 0%, #256974 100%)' }
                      }}
                    >
                      Live Mobile Preview
                    </Button>
                  </Box>

                  {/* Normal Form Grid: Left = Selected Banner + Choose Template, Right = Upload Custom Template */}
                  <Box display="flex" flexWrap="wrap" gap={2} justifyContent="space-between">
                    {/* Left Column: Current Banner & Choose Template */}
                    <Box flex="1 1 48%">
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.8}>
                        <Box display="flex" alignItems="center">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: isLight ? '#475569' : '#CBD5E1' }}>
                            Current Banner Image *
                          </Typography>
                          <HelpTooltip
                            title="Selected Banner"
                            purpose="The banner image that will appear at the top of this prayer."
                            howItWorks="Click 'Choose from Templates' to browse all templates in a popup modal."
                            mobileEffect="Displayed as full-width hero image on mobile."
                          />
                        </Box>
                        <Button
                          size="small"
                          startIcon={<CollectionsBookmarkIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setTemplateModalOpen(true)}
                          sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            color: '#64D2E2',
                            backgroundColor: 'rgba(76, 161, 175, 0.15)',
                            border: '1px solid rgba(76, 161, 175, 0.35)',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            py: 0.3,
                            px: 1.5,
                            '&:hover': {
                              backgroundColor: 'rgba(76, 161, 175, 0.28)',
                              borderColor: '#64D2E2',
                            }
                          }}
                        >
                          Choose from Templates ({templatesList.length})
                        </Button>
                      </Box>

                      {/* Banner Preview Display (normal field styling) */}
                      <Box
                        onClick={() => setTemplateModalOpen(true)}
                        sx={{
                          width: '100%',
                          height: 85,
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          border: isLight ? '1px solid #CBD5E1' : '1px solid rgba(255, 255, 255, 0.15)',
                          backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: '#2DD4BF', boxShadow: '0 2px 10px rgba(45, 212, 191, 0.2)' }
                        }}
                      >
                        {customTemplatePreview ? (
                          <img
                            src={customTemplatePreview}
                            alt="Custom Banner Preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        ) : currentActiveTemplate?.url ? (
                          <img
                            src={getTemplateImageUrl(currentActiveTemplate.url, Image_URL)}
                            alt="Selected Template"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => {
                              const fallbackPath = currentActiveTemplate.url.startsWith('/') ? currentActiveTemplate.url : `/${currentActiveTemplate.url}`;
                              if (!e.currentTarget.src.endsWith(fallbackPath)) {
                                e.currentTarget.src = fallbackPath;
                              }
                            }}
                          />
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="center" height="100%" gap={1}>
                            <CollectionsBookmarkIcon sx={{ color: '#64D2E2', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                              Click to choose a banner template ({templatesList.length} available)
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 6,
                            left: 8,
                            right: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Chip
                            size="small"
                            label={customTemplatePreview ? "Custom Banner Loaded" : (currentActiveTemplate?.title || "Template Selected")}
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(0,0,0,0.75)',
                              color: '#FFFFFF',
                              backdropFilter: 'blur(4px)'
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.72rem', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>
                            Click to change ↗
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Right Column: Custom Template Upload (Normal field styling, NO dashed box!) */}
                    <Box flex="1 1 48%">
                      <Box display="flex" alignItems="center" mb={0.8}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: isLight ? '#475569' : '#CBD5E1' }}>
                          Custom Banner Upload (Optional)
                        </Typography>
                        <HelpTooltip
                          title="Upload Custom Banner"
                          purpose="Provide your own custom prayer banner image instead of choosing from library templates."
                          howItWorks="Enter an optional template title and click Upload Image."
                          mobileEffect="Displays your uploaded banner on mobile."
                        />
                      </Box>

                      {/* Normal row: TextField + Upload Image Button */}
                      <Box display="flex" gap={1.2} alignItems="center">
                        <TextField
                          placeholder="Custom Template Title"
                          size="small"
                          fullWidth
                          value={customTemplateTitle}
                          onChange={(e) => setCustomTemplateTitle(e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              backgroundColor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.05)",
                              height: 40,
                            }
                          }}
                        />
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<UploadFileIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />}
                          sx={{
                            whiteSpace: "nowrap",
                            height: 40,
                            px: 2,
                            borderRadius: "8px",
                            background: "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            textTransform: "none",
                            flexShrink: 0,
                            boxShadow: "0 2px 6px rgba(13, 148, 136, 0.35)",
                            "&:hover": { background: "#0F766E" }
                          }}
                        >
                          Upload Image
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleCustomTemplateUpload}
                          />
                        </Button>
                      </Box>

                      {customTemplatePreview && (
                        <Box mt={1} display="flex" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={customTemplateFile ? customTemplateFile.name : "Custom Image Loaded"}
                            color="success"
                            onDelete={() => {
                              setCustomTemplatePreview(null);
                              setCustomTemplateFile(null);
                              setCustomTemplateTitle('');
                            }}
                            sx={{ fontWeight: 600, fontSize: "0.72rem", height: 24 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* ── Template Selection Modal Popup ── */}
                <Dialog
                  open={templateModalOpen}
                  onClose={() => setTemplateModalOpen(false)}
                  maxWidth="md"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: '16px',
                      background: isLight ? '#FFFFFF' : '#141d26',
                      border: isLight ? '1px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.3)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                      overflow: 'hidden'
                    }
                  }}
                >
                  <DialogTitle
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
                      py: 2,
                      px: 3,
                      background: isLight ? '#F8FAFC' : 'rgba(255,255,255,0.02)'
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '1.1rem' }}>
                        Choose Prayer Banner Template
                      </Typography>
                      <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)', fontSize: '0.78rem' }}>
                        Click a template to apply it as your prayer closet banner ({templatesList.length} templates available)
                      </Typography>
                    </Box>
                    <IconButton onClick={() => setTemplateModalOpen(false)} size="small" sx={{ color: isLight ? '#64748B' : '#94A3B8' }}>
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>

                  <DialogContent sx={{ p: 3, maxHeight: '65vh' }}>
                    {templatesList.length === 0 ? (
                      <Box py={6} textAlign="center">
                        <Typography variant="body1" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)' }}>
                          No templates available. You can upload a custom banner image instead.
                        </Typography>
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        {templatesList.map((template) => {
                          const isSelected = String(selectedTemplateId) === String(template.id) && !customTemplatePreview;
                          return (
                            <Grid item xs={12} sm={6} md={4} key={template.id}>
                              <Box
                                onClick={() => {
                                  setSelectedTemplateId(template.id);
                                  setCustomTemplatePreview(null);
                                  setCustomTemplateFile(null);
                                  setTemplateModalOpen(false);
                                }}
                                sx={{
                                  position: 'relative',
                                  border: isSelected
                                    ? '2.5px solid #2DD4BF'
                                    : isLight ? '1.5px solid #E2E8F0' : '1.5px solid rgba(255, 255, 255, 0.12)',
                                  borderRadius: '12px',
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  height: 140,
                                  boxShadow: isSelected ? '0 0 16px rgba(45, 212, 191, 0.4)' : '0 2px 6px rgba(0,0,0,0.1)',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    borderColor: '#2DD4BF',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 18px rgba(0,0,0,0.25)'
                                  }
                                }}
                              >
                                <img
                                  src={getTemplateImageUrl(template.url, Image_URL)}
                                  alt={template.title}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block'
                                  }}
                                  onError={(e) => {
                                    const fallbackPath = template.url.startsWith('/') ? template.url : `/${template.url}`;
                                    if (!e.currentTarget.src.endsWith(fallbackPath)) {
                                      e.currentTarget.src = fallbackPath;
                                    }
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    p: 1,
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.78rem' }} noWrap>
                                    {template.title || `Template #${template.id}`}
                                  </Typography>
                                  {isSelected && (
                                    <Chip
                                      size="small"
                                      label="Selected"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.65rem',
                                        fontWeight: 800,
                                        bgcolor: '#2DD4BF',
                                        color: '#042F2E',
                                      }}
                                    />
                                  )}
                                </Box>
                                {/* Delete button if user has permission */}
                                {canDeleteTemplate(template) && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(template);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 6,
                                      right: 6,
                                      backgroundColor: 'rgba(0,0,0,0.65)',
                                      color: '#EF4444',
                                      p: 0.5,
                                      '&:hover': { backgroundColor: 'rgba(239,68,68,0.9)', color: '#fff' }
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </DialogContent>

                  <DialogActions sx={{ px: 3, py: 1.5, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)' }}>
                    <Button
                      onClick={() => setTemplateModalOpen(false)}
                      sx={{ color: isLight ? '#64748B' : '#94A3B8', textTransform: 'none', fontWeight: 600 }}
                    >
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>

              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Modal for Basic Information User Example & Live Preview */}
          <MobileSpecialPrayerModal
            open={basicInfoModal.open}
            onClose={() => setBasicInfoModal({ ...basicInfoModal, open: false })}
            isExample={basicInfoModal.mode === 'example'}
            title={title}
            bannerImage={
              customTemplatePreview ||
              (currentActiveTemplate?.url ? Image_URL + currentActiveTemplate.url : "")
            }
            duration={numDays || numberOfDays || 40}
            format={prayerType}
            description={description}
          />









          {/* Day-wise Content Section */}
          <Accordion defaultExpanded sx={{ border: '1px solid #256974ff', borderRadius: '12px !important', boxShadow: 'none', '&:before': { display: 'none' }, overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} sx={{ background: '#256974ff', borderBottom: '1px solid #256974ff' }}>
              <Typography fontWeight={600} color="white">Day-Wise Content ({numDays} Days)</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>

              {/* Quick Actions & Excel Import Toolbar */}
              <Box sx={{ mb: 2, p: 2, background: isLight ? 'rgba(13, 148, 136, 0.04)' : 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(13, 148, 136, 0.25)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sameContentForAllDays}
                        onChange={(e) => handleToggleSameContent(e.target.checked)}
                        sx={{
                          color: '#0D9488',
                          '&.Mui-checked': { color: '#0D9488' }
                        }}
                      />
                    }
                    label={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#F1F5F9', fontSize: '0.92rem' }}>
                          Same prayer for all days
                        </Typography>
                        <HelpTooltip
                          title="Same prayer for all days"
                          purpose="When checked, Day 1 content (title, description, translations, and prayer points) is automatically used for all days."
                          howItWorks="You configure Day 1, and every day in the journey will deliver the exact same content."
                          mobileEffect="Intercessors see the same devotion and prayer points on every day of the prayer."
                        />
                      </Box>
                    }
                  />

                  <Box sx={{ width: '1px', height: '24px', bgcolor: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)', mx: 0.5 }} />

                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4E8D9E', mr: 0.5 }}>
                    ⚡ Quick Actions:
                  </Typography>

                  <Box display="flex" alignItems="center">
                    <Tooltip title="Copies Day 1's title, description, and prayer points to all remaining days">
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleApplyDay1ToAll}
                          disabled={numDays <= 1 || !(dayWiseData[1]?.prayerPoints?.length > 0 || dayWiseData[1]?.title)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            borderColor: '#4E8D9E',
                            color: '#4E8D9E',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#6FB4C6', background: 'rgba(78, 141, 158, 0.1)' }
                          }}
                        >
                          Apply Day 1 to All Days
                        </Button>
                      </span>
                    </Tooltip>
                    <HelpTooltip
                      title="Apply Day 1 to All Days"
                      purpose="Instantly clones Day 1's title, description, translations, and prayer points into every subsequent day."
                      howItWorks="Copies Day 1 content into Days 2, 3, etc. as an editable starting point, saving you from entering repeated information."
                      mobileEffect="Intercessors see consistent daily content and starter points across all days of the prayer."
                    />
                  </Box>

                  {currentDay && currentDay < numDays && !sameContentForAllDays && (
                    <Box display="flex" alignItems="center">
                      <Tooltip title={`Duplicate Day ${currentDay} content to Day ${currentDay + 1}`}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyCurrentToNext}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            borderColor: 'rgba(255,255,255,0.3)',
                            color: '#E0E7EC',
                            '&:hover': { borderColor: '#FFFFFF', background: 'rgba(255,255,255,0.05)' }
                          }}
                        >
                          Copy Day {currentDay} → Day {currentDay + 1}
                        </Button>
                      </Tooltip>
                      <HelpTooltip
                        title={`Copy Day ${currentDay} → Day ${currentDay + 1}`}
                        purpose={`Copies everything from Day ${currentDay} directly into Day ${currentDay + 1}.`}
                        howItWorks="Duplicates the title, description, and prayer points from the currently selected day to the next day."
                        mobileEffect="Allows consecutive days in the prayer journey to build seamlessly on the previous day's theme."
                      />
                    </Box>
                  )}

                  <Box display="flex" alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PhoneIphoneIcon sx={{ fontSize: 18 }} />}
                      onClick={() => {
                        setPreviewModalMode('example');
                        setMobilePreviewOpen(true);
                      }}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        px: 1.8,
                        boxShadow: '0 2px 8px rgba(15, 118, 110, 0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #115E59 0%, #0F766E 100%)' }
                      }}
                    >
                      User Example
                    </Button>
                    <HelpTooltip
                      title="User Example"
                      purpose="Interactive mobile app simulator showing 2 standard reference prayer points as examples."
                      howItWorks="Renders a smartphone mockup with 2 reference prayer points so admins can preview the standard user experience."
                      mobileEffect="Provides standard design and layout reference before writing customized points."
                    />
                  </Box>
                </Box>

                <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Download sample Excel format for filling prayer points">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon />}
                        onClick={downloadDayPointsTemplate}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          borderColor: 'rgba(255,255,255,0.2)',
                          color: '#8E9FA9',
                          '&:hover': { borderColor: '#4E8D9E', color: '#4E8D9E' }
                        }}
                      >
                        Points Template (.xlsx)
                      </Button>
                    </Tooltip>
                    <HelpTooltip
                      title="Points Template (.xlsx)"
                      purpose="Downloads a structured Excel spreadsheet (.xlsx) template for entering prayer points offline."
                      howItWorks="Open the template in Excel or Google Sheets, fill in columns (Day Number, Point Title, Point Description), and save."
                      mobileEffect="Ensures imported prayer points are cleanly mapped to each day with proper structure."
                    />
                  </Box>

                  <Box display="flex" alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      component="label"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        background: '#177373',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        '&:hover': { background: '#1C8A8A' }
                      }}
                    >
                      Import Points from Excel
                      <input type="file" hidden accept=".xlsx, .xls" onChange={handleImportPointsExcel} />
                    </Button>
                    <HelpTooltip
                      title="Import Points from Excel"
                      purpose="Bulk upload prayer points for all days at once from a completed Excel spreadsheet."
                      howItWorks="Click and select your filled .xlsx file. The system parses all rows and organizes points into their respective days automatically."
                      mobileEffect="Rapidly prepares multi-day prayer guides with dozens of prayer points without tedious manual typing."
                    />
                  </Box>
                </Box>
              </Box>

              {/* Day selection buttons with points count badges */}
              <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', pb: 2, display: 'flex', gap: 1 }}>
                {Array.from({ length: numDays }, (_, i) => i + 1).map((day) => {
                  const pointsCount = dayWiseData[day]?.prayerPoints?.length || 0;
                  const isFilled = pointsCount > 0 || !!dayWiseData[day]?.title;
                  return (
                    <Button
                      key={day}
                      variant={currentDay === day ? "contained" : "outlined"}
                      onClick={() => handleOpenDay(day)}
                      sx={{
                        borderRadius: "20px",
                        fontWeight: 600,
                        textTransform: "none",
                        minWidth: '110px',
                        flexShrink: 0,
                        backgroundColor: currentDay === day ? '#177373 !important' : (isFilled ? 'rgba(23, 115, 115, 0.15)' : 'transparent'),
                        borderColor: currentDay === day ? '#177373' : (isFilled ? '#4E8D9E' : '#475569'),
                        color: currentDay === day ? '#FFFFFF' : (isFilled ? '#6FB4C6' : '#94A3B8'),
                        '&:hover': {
                          backgroundColor: currentDay === day ? '#1C8A8A' : 'rgba(255,255,255,0.05)',
                        }
                      }}
                    >
                      Day {day} {pointsCount > 0 ? `(${pointsCount})` : ''}
                    </Button>
                  );
                })}
              </Box>

              {/* ── SINGLE GLOBAL LANGUAGE SELECTOR (Shown only once) ── */}
              {currentDay && (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    p: 1.5,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.12)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800, color: isLight ? "#0F766E" : "#2DD4BF", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                    🌐 Language:
                  </Typography>

                  <Autocomplete
                    size="small"
                    options={SUPPORTED_LANGUAGES}
                    value={SUPPORTED_LANGUAGES.find(l => l.code === activeContentLang) || { code: activeContentLang, label: getActiveLangLabel() }}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.label}`}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    onChange={(e, val) => {
                      const code = val?.code || (typeof val === 'string' ? val : 'en');
                      setActiveContentLang(code);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type to search language (e.g. Telugu, Tamil)..."
                        sx={{
                          width: 280,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            height: 36,
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    )}
                  />

                  {activeContentLang !== "en" && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleTranslateAllForDay(activeContentLang)}
                      sx={{
                        bgcolor: "#0F766E",
                        color: "#FFFFFF",
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "8px",
                        height: 36,
                        px: 2,
                        '&:hover': { bgcolor: "#115E59" }
                      }}
                    >
                      Auto-Translate from English
                    </Button>
                  )}

                  <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "#94A3B8", fontSize: "0.75rem", fontStyle: "italic" }}>
                    Selected language applies to Day {currentDay} devotion and all prayer points below.
                  </Typography>
                </Box>
              )}

              {currentDay && (
                <Accordion defaultExpanded sx={{ mt: 2, border: '1px dashed #cbd5e1', borderRadius: 2, boxShadow: 'none', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: '#f8fafc', borderBottom: '1px dashed #cbd5e1' }}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" width="100%">
                      <Typography fontWeight={700} color="#334155" fontSize="1rem">
                        Day {currentDay} Configuration ({getActiveLangLabel()})
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ mb: 2, border: "1px solid #cbd5e1", borderRadius: 2, p: 2 }}>
                        {getThemesForCurrentDay().map((theme, tIdx) => (
                          <Box
                            key={tIdx}
                            sx={{
                              mb: 2,
                              p: tIdx > 0 ? 2 : 0,
                              borderRadius: 2,
                              bgcolor: tIdx > 0 ? (isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)') : 'transparent',
                              border: tIdx > 0 ? (isLight ? '1px dashed #CBD5E1' : '1px dashed rgba(255, 255, 255, 0.15)') : 'none',
                              position: 'relative',
                            }}
                          >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 700,
                                  color: isLight ? "#0F766E" : "#64D2E2",
                                  fontSize: "0.85rem",
                                }}
                              >
                                Prayer Theme {tIdx + 1}
                              </Typography>
                              {tIdx > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveTheme(tIdx)}
                                  sx={{ color: "#EF4444", p: 0.5 }}
                                  title={`Remove Prayer Theme ${tIdx + 1}`}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </Box>

                            <TextField
                              label={`Prayer Theme ${tIdx + 1} (${getActiveLangLabel()})`}
                              fullWidth
                              sx={{ mb: 1.5 }}
                              value={theme[getDayTitleField()] || theme.title || ""}
                              onChange={(e) => handleThemeChange(tIdx, getDayTitleField(), e.target.value)}
                            />
                            <TextField
                              label={`Prayer Theme Description ${tIdx + 1} (${getActiveLangLabel()})`}
                              fullWidth
                              multiline
                              minRows={2}
                              sx={{ mb: 1.5 }}
                              value={theme[getDayDescField()] || theme.description || ""}
                              onChange={(e) => handleThemeChange(tIdx, getDayDescField(), e.target.value)}
                            />
                          </Box>
                        ))}

                        <Box display="flex" justifyContent="flex-start" mb={2}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                            onClick={handleAddTheme}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: isLight ? "#0F766E" : "#64D2E2",
                              borderColor: isLight ? "#0F766E" : "#64D2E2",
                              fontSize: "0.82rem",
                              "&:hover": {
                                bgcolor: isLight ? "rgba(15, 118, 110, 0.08)" : "rgba(100, 210, 226, 0.1)",
                              },
                            }}
                          >
                            + Add Prayer Theme
                          </Button>
                        </Box>

                        {/* One-Line Action Toolbar: Upload Image, Add Video */}
                        <PrayerVideoManager
                          dayNumber={currentDay}
                          videos={dayWiseData[currentDay]?.videos || []}
                          isLight={isLight}
                          extraControls={
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={<UploadFileIcon sx={{ fontSize: 18, color: isLight ? '#0F766E' : '#2DD4BF' }} />}
                                sx={{
                                  borderColor: isLight ? '#0D9488' : '#2DD4BF',
                                  color: isLight ? '#0F766E' : '#FFFFFF',
                                  backgroundColor: isLight ? 'rgba(15, 118, 110, 0.08)' : 'rgba(45, 212, 191, 0.16)',
                                  fontWeight: 700,
                                  fontSize: '0.84rem',
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  height: 36,
                                  px: 2,
                                  boxShadow: isLight ? 'none' : '0 1px 4px rgba(0,0,0,0.25)',
                                  '&:hover': {
                                    borderColor: isLight ? '#0D9488' : '#38BDF8',
                                    backgroundColor: isLight ? 'rgba(15, 118, 110, 0.16)' : 'rgba(45, 212, 191, 0.28)',
                                    color: '#FFFFFF',
                                  }
                                }}
                              >
                                Upload Image
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*"
                                  onChange={(e) => handleInputChange("image", e.target.files[0])}
                                />
                              </Button>
                              <HelpTooltip
                                title="Devotional Image"
                                purpose="Upload an inspiring background banner or artwork for this specific prayer day."
                                howItWorks="Accepts JPEG, PNG, or WebP. Replaces default prayer background."
                                mobileEffect="Displays as the featured visual header card on the mobile app for this day."
                              />
                              {dayWiseData[currentDay]?.image && (
                                <Chip
                                  size="small"
                                  label={dayWiseData[currentDay].image.name}
                                  onDelete={() => handleInputChange("image", null)}
                                  sx={{ maxWidth: 140, fontSize: '0.75rem' }}
                                />
                              )}
                            </Box>
                          }
                          onVideosChange={(updatedVideos) => {
                            handleInputChange("videos", updatedVideos);
                            const firstPublished = updatedVideos.find((v) => v.is_published);
                            handleInputChange("video_url", firstPublished ? firstPublished.url : "");
                            handleInputChange("video_title", firstPublished ? firstPublished.title : "");
                            handleInputChange("video_thumbnail", firstPublished ? firstPublished.thumbnail : "");
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Prayer Points (Language applied from top single selector) */}
                    {(dayWiseData[currentDay]?.prayerPoints || []).map((item, index) => (
                      <Box key={index} sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mb: 2 }}>
                        <TextField
                          label={`Point #${index + 1} Title (${getActiveLangLabel()})`}
                          fullWidth
                          sx={{ mb: 2 }}
                          value={item[getPointTitleField()] || ""}
                          onChange={(e) => handleDailyPrayerPointChange(index, getPointTitleField(), e.target.value)}
                        />
                        <TextField
                          label={`Point #${index + 1} Description (${getActiveLangLabel()})`}
                          fullWidth
                          multiline
                          minRows={2}
                          sx={{ mb: 2 }}
                          value={item[getPointDescField()] || ""}
                          onChange={(e) => handleDailyPrayerPointChange(index, getPointDescField(), e.target.value)}
                        />

                        <Box textAlign="right">
                          <Button variant="outlined" color="error" size="small" onClick={() => removePrayerPoint(index)}>
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    ))}

                    {/* 2.4 How many prayer points do you have generator */}
                    <Box
                      sx={{
                        p: 2,
                        mt: 2,
                        borderRadius: "12px",
                        bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                        border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800} color={isLight ? "#0F172A" : "#FFFFFF"}>
                          How many prayer points do you have?
                        </Typography>
                        <Typography variant="caption" color={isLight ? "#64748B" : "rgba(255,255,255,0.7)"}>
                          Select or enter the count to dynamically generate prayer point fields.
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {/* Quick count buttons */}
                        <Box display="flex" gap={0.5}>
                          {[1, 2, 3, 5, 7, 10].map((cnt) => (
                            <Button
                              key={cnt}
                              size="small"
                              variant={(dayWiseData[currentDay]?.prayerPoints || []).length === cnt ? "contained" : "outlined"}
                              onClick={() => handleSetPrayerPointsCount(cnt)}
                              sx={{
                                minWidth: 32,
                                height: 32,
                                p: 0,
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                borderRadius: "8px",
                                color: (dayWiseData[currentDay]?.prayerPoints || []).length === cnt
                                  ? "#FFFFFF"
                                  : isLight ? "#0F766E" : "#64D2E2",
                                borderColor: isLight ? "rgba(13,148,136,0.5)" : "rgba(100,210,226,0.4)",
                                bgcolor: (dayWiseData[currentDay]?.prayerPoints || []).length === cnt
                                  ? "#0F766E"
                                  : "transparent",
                                '&:hover': {
                                  bgcolor: (dayWiseData[currentDay]?.prayerPoints || []).length === cnt
                                    ? "#0D6B63"
                                    : isLight ? "rgba(13,148,136,0.1)" : "rgba(100,210,226,0.1)",
                                  borderColor: "#0D9488",
                                },
                              }}
                            >
                              {cnt}
                            </Button>
                          ))}
                        </Box>

                        {/* Numeric input */}
                        <TextField
                          size="small"
                          type="number"
                          value={(dayWiseData[currentDay]?.prayerPoints || []).length || ""}
                          placeholder="Count"
                          onChange={(e) => handleSetPrayerPointsCount(e.target.value)}
                          inputProps={{ min: 0, max: 50, style: { textAlign: "center", width: 45, padding: "6px" } }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: "8px",
                              height: 32,
                            }
                          }}
                        />

                        {/* Add single prayer point button */}
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={addPrayerPoint}
                          sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, height: 32 }}
                        >
                          + Add One More
                        </Button>
                      </Box>
                    </Box>

                    {/* ── User Preview Section (Shows admin-added things) ── */}
                    <Box
                      sx={{
                        p: 2.2,
                        mt: 2.5,
                        borderRadius: "12px",
                        bgcolor: isLight ? "rgba(13, 148, 136, 0.06)" : "rgba(13, 148, 136, 0.14)",
                        border: isLight ? "1.5px solid rgba(13, 148, 136, 0.3)" : "1.5px solid rgba(13, 148, 136, 0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIphoneIcon sx={{ fontSize: 20, color: isLight ? "#0D9488" : "#64D2E2" }} />
                          <Typography variant="subtitle2" fontWeight={800} color={isLight ? "#0F172A" : "#FFFFFF"}>
                            User Preview (Live Prayer Content)
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)", mt: 0.3, display: "block" }}>
                          Preview your entered prayer themes and {(dayWiseData[currentDay]?.prayerPoints || []).length} prayer point(s) as mobile users will see them.
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        startIcon={<PhoneIphoneIcon sx={{ fontSize: 18 }} />}
                        onClick={() => {
                          setPreviewModalMode('preview');
                          setMobilePreviewOpen(true);
                        }}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          px: 2.2,
                          py: 0.8,
                          boxShadow: '0 2px 8px rgba(15, 118, 110, 0.3)',
                          '&:hover': { background: 'linear-gradient(135deg, #115E59 0%, #0F766E 100%)' }
                        }}
                      >
                        User Preview
                      </Button>
                    </Box>

                  </AccordionDetails>


                </Accordion>
              )}

              {formErrors.content && (
                <Typography color="error">{formErrors.content}</Typography>
              )}
            </AccordionDetails>
          </Accordion>






          {/* Save Button Section */}
          <Box
            mt={4}
            sx={{
              pt: 2.5,
              borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1.2,
            }}
          >
            {/* Workflow note */}
            <Box
              sx={{
                width: '100%',
                p: 1.8,
                borderRadius: '10px',
                bgcolor: isLight ? 'rgba(13, 148, 136, 0.06)' : 'rgba(13, 148, 136, 0.12)',
                border: isLight ? '1px solid rgba(13, 148, 136, 0.25)' : '1px solid rgba(13, 148, 136, 0.35)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: '#0D9488',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '0.75rem',
                  fontWeight: 800,
                  mt: 0.15,
                }}
              >
                i
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700} color={isLight ? '#0F172A' : '#FFFFFF'} mb={0.3}>
                  Flexible Saving — No Required Fields to Create
                </Typography>
                <Typography variant="caption" sx={{ color: isLight ? '#475569' : 'rgba(255,255,255,0.7)', lineHeight: 1.5, display: 'block' }}>
                  Click <strong>"Save to Pending Reviews"</strong> at any point — even with incomplete fields.
                  The prayer is saved as <em>inactive</em> and will NOT go live until you click <strong>Publish</strong> from the Pending Reviews tab.
                  Complete missing fields later by editing the prayer before publishing.
                </Typography>
              </Box>
            </Box>

            {/* Action buttons row */}
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  color: isLight ? "#475569" : "rgba(255,255,255,0.75)",
                  borderColor: isLight ? "#CBD5E1" : "rgba(255,255,255,0.25)",
                  borderRadius: '30px',
                  px: 3.5,
                  py: 1.1,
                  fontSize: '0.9rem',
                  fontWeight: 650,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: isLight ? "#94A3B8" : "rgba(255,255,255,0.45)",
                    bgcolor: isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.06)",
                    color: isLight ? "#1E293B" : "#FFFFFF",
                  }
                }}
              >
                Cancel & Save Draft
              </Button>

              <Tooltip title="Save as a pending review — complete remaining fields and publish when ready" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleAddDevotion(false)}
                  sx={{
                    background: 'linear-gradient(135deg, #256974ff 0%, #047857 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #1e5a63 0%, #036044 100%)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' },
                    px: 5,
                    py: 1.1,
                    borderRadius: '30px',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 2px 10px rgba(16, 185, 129, 0.2)'
                  }}
                >
                  💾 Save to Pending Reviews
                </Button>
              </Tooltip>
            </Box>
          </Box>
          </Box>
        </>
      )}
      <Toast toast={toast} setToast={setToast} /> {/* Added Toast component */}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            p: 1
          },
        }}
      >
        <DialogTitle sx={{ color: '#FFFFFF', fontWeight: 700 }}>
          Confirm Delete Template
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
            Are you sure you want to delete the template "{templateToDelete?.title}"?
          </Typography>
          <Typography sx={{ color: '#f59e0b', fontSize: '0.875rem', fontStyle: 'italic' }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#FFFFFF' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              background: 'rgba(239, 68, 68, 0.2)',
              color: '#EF4444',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              '&:hover': { background: 'rgba(239, 68, 68, 0.3)' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drafts Modal */}
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        moduleName="Special Prayer"
        drafts={draftsList}
        onContinueEditing={handleContinueDraft}
        onDeleteDraft={handleDeleteDraft}
        onClearAllDrafts={() => {
          DraftManager.clearAllDrafts('special-prayer', currentOrgKey);
          setDraftsList([]);
          setActiveDraftId(null);
          setToast({ open: true, message: "All drafts cleared.", severity: "info" });
        }}
      />

      <ModuleSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        moduleKey="special_prayers"
        moduleTitle="Prayer Closet"
      />

      {/* ── 2.5 MOBILE PRAYER PREVIEW MODAL ── */}
      <MobilePrayerPreviewModal
        open={mobilePreviewOpen}
        onClose={() => setMobilePreviewOpen(false)}
        prayerTitle={title || "Pray for Christians"}
        dayNumber={currentDay || 1}
        totalDays={numDays || 21}
        dayData={dayWiseData[currentDay || 1] || {}}
        prayerPoints={dayWiseData[currentDay || 1]?.prayerPoints || []}
        timeSlot={`${fromTime || "13:30"} - ${toTime || "14:00"}`}
        dayWiseData={dayWiseData}
        fromDate={fromDate}
        mode={previewModalMode}
        talkItOver={talkItOver}
      />

      {/* ── 2.6 PENDING / PUBLISHED SUCCESS MODAL ── */}
      <Dialog
        open={reviewSuccessModalOpen}
        onClose={() => navigate('/admin/special-prayers')}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1,
            bgcolor: isLight ? "#FFFFFF" : "#182232",
            border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.4)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3, pb: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: isLight ? (savedAsPublished ? "#ECFDF5" : "#EFF6FF") : (savedAsPublished ? "rgba(16, 185, 129, 0.2)" : "rgba(59, 130, 246, 0.2)"),
              color: savedAsPublished ? "#10B981" : "#3B82F6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
              boxShadow: savedAsPublished ? "0 0 20px rgba(16, 185, 129, 0.25)" : "0 0 20px rgba(59, 130, 246, 0.25)",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} color={isLight ? "#0F172A" : "#FFFFFF"}>
            {savedAsPublished ? "Prayer Published Successfully! 🎉" : "Prayer Saved for Review!"}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", px: 4, pb: 2 }}>
          <Typography variant="body1" color={isLight ? "#475569" : "rgba(255,255,255,0.85)"} mb={2}>
            {savedAsPublished
              ? <>Your prayer closet <strong>"{title}"</strong> is now <strong>live on the mobile app</strong> and visible to believers.</>  
              : <>Your prayer closet campaign <strong>"{title}"</strong> has been saved as an <strong>inactive draft pending review</strong>.</>  
            }
          </Typography>
          <Box
            sx={{
              p: 2,
              borderRadius: "12px",
              bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
              border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
              textAlign: "left",
              mb: 2,
            }}
          >
            <Typography variant="caption" fontWeight={700} color="#0D9488" display="block" mb={0.5}>
              {savedAsPublished ? "WHAT'S LIVE NOW" : "WHAT HAPPENS NEXT?"}
            </Typography>
            <Typography variant="body2" color={isLight ? "#334155" : "rgba(255,255,255,0.8)"} component="div">
              {savedAsPublished ? (
                <>
                  • The prayer is now active and visible in the mobile app.<br />
                  • Believers can join and pray immediately.<br />
                  • You can edit or manage it from the Active tab in Prayer Closet.
                </>
              ) : (
                <>
                  • It will NOT appear in the mobile app until published.<br />
                  • You can review and complete missing fields by editing the prayer.<br />
                  • Once finalized, click <strong>Publish</strong> in Pending Reviews to launch the prayer journey to believers.
                </>
              )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, display: "flex", justifyContent: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setReviewSuccessModalOpen(false);
              setSavedAsPublished(false);
              setTitle("");
              setDescription("");
              setDayWiseData({});
              window.location.reload();
            }}
            sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}
          >
            Create Another Prayer
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(savedAsPublished ? '/admin/special-prayers?tab=active' : '/admin/special-prayers?tab=pending')}
            sx={{
              borderRadius: "10px",
              fontWeight: 800,
              textTransform: "none",
              bgcolor: savedAsPublished ? "#0F766E" : "#0F766E",
              px: 3,
              '&:hover': { bgcolor: "#115E59" }
            }}
          >
            {savedAsPublished ? "View Active Prayers →" : "Go to Pending Reviews →"}
          </Button>
        </DialogActions>
      </Dialog>

      <Toast toast={toast} setToast={setToast} />
    </Box>
  );
};

export default AddSpecialPrayer;
